import React, { useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router';
import moment from 'moment';

import './styles.scss';
import { useSwarm } from '@lbs/swarm-client';
import { getRacingsList, getFutureAndSpecialRacingList } from '@racings/api/requests';
import { asyncWrapper } from '@common/hocs/asyncWrapper';
import { racingsListByRegionSelector } from '@racings/api/selectors';
import { SportTypesIds, SportTypesUrlIds } from '@racings/constants/enums/sportTypes';
import { RacingsContext } from '@racings/store/context';
import RacingItem from './racingItem';
import { RacingFutureSpecialItems } from './racingFutureSpecialItems';
import { ascAlphabeticalSort } from '@common/helpers/declarative/sortings';

export const RacingsList = asyncWrapper(({ routePrefix }) => {
  const navigate = useNavigate();
  const { sportType, regionAlias } = useParams();
  const [{ dateFilter, id }] = useContext(RacingsContext);
  const [searchText] = useOutletContext();

  const activeSportId = SportTypesUrlIds[sportType] || SportTypesIds.HORSE;
  let racingsList;
  if (id) {
    racingsList = racingsListByRegionSelector(useSwarm(getFutureAndSpecialRacingList(id)));
  } else {
    racingsList = racingsListByRegionSelector(useSwarm(getRacingsList(activeSportId, dateFilter)));
  }
  const onSelectGame = (e, routePrefix, regAlias, regId, compId, gameId, special) => {
    e.stopPropagation();

    navigate(`${routePrefix}/racings/${sportType}/upComing/${regAlias}/${compId}/${gameId}/${special ? 'special_futures' : ''}`);
  };

  const racingsListFiltered = useMemo(() => {
    const currentTime = moment().unix();
    
    if (!searchText && regionAlias == null) {
      return racingsList.reduce((acc, region) => {
        const filteredCompetitions = region.competitions?.map((competition) => ({
          ...competition,
          games: competition.games?.filter((game) => game?.start_ts >= currentTime) ?? []
        })).filter((competition) => competition.games.length > 0) ?? [];

        if (filteredCompetitions.length > 0) {
          acc.push({
            ...region,
            competitions: filteredCompetitions,
          });
        }

        return acc;
      }, []);
    }

    const lowerCaseSearch = searchText?.toLowerCase() ?? '';

    const newList = racingsList.reduce((acc, region) => {
      const matchesRegion = regionAlias != null
        ? String(region.alias) === String(regionAlias)
        : true;

      if (!matchesRegion) return acc;

      const filteredCompetitions = region.competitions?.filter((competition) => {
        const compName = competition?.name?.toLowerCase?.() ?? '';
        const matchesSearch = searchText
          ? compName.includes(lowerCaseSearch)
          : true;
        return matchesSearch;
      }).map((competition) => ({
        ...competition,
        games: competition.games?.filter((game) => game?.start_ts >= currentTime) ?? []
      })).filter((competition) => competition.games.length > 0) ?? [];

      if (filteredCompetitions.length > 0) {
        acc.push({
          ...region,
          competitions: filteredCompetitions,
        });
      }

      return acc;
    }, []);

    return newList;
  }, [searchText, racingsList, regionAlias]);
  return (
    <div className="racings-list-container">
      {racingsListFiltered.sort(ascAlphabeticalSort('name')).map((region) => {
        const Component = [180, 153].includes(id) ? RacingFutureSpecialItems : RacingItem;
        return (
          <Component
            routePrefix={routePrefix}
            alwaysOpen={racingsListFiltered.length === 1}
            onSelectGame={onSelectGame}
            key={region.id}
            region={region}
            search={searchText}
            sportType={sportType}
            id={id}
          />
        );
      })}
    </div>
  );
});
